import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ImageList,
  ImageListItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import type { PostResponse, PostUpdate } from "@bw/shared/types/generated";
import { useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useUpdatePost } from "../hooks/useUpdatePost";
import { MAX_MEDIA_PER_POST, uploadPostPhoto } from "../lib/photoUpload";

interface EditPhoto {
  id: string;
  publicUrl: string;
  mediaType: "image" | "youtube";
}

export function EditPostDialog({
  post,
  onClose,
}: {
  post: PostResponse;
  onClose: () => void;
}) {
  const update = useUpdatePost();
  const isRepost = post.reposted_post != null;
  const originalMedia = post.media ?? [];
  const originalUrls = originalMedia.map((item) => item.url);
  const [content, setContent] = useState(post.content ?? "");
  const [photos, setPhotos] = useState<EditPhoto[]>(() =>
    isRepost
      ? []
      : originalMedia.map((item, index) => ({
          id: `photo-${index}`,
          publicUrl: item.url,
          mediaType: item.media_type,
        })),
  );
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const photoSeqRef = useRef(originalUrls.length);

  const trimmed = content.trim();
  const atCapacity = photos.length >= MAX_MEDIA_PER_POST;
  const contentChanged = trimmed !== (post.content ?? "");
  const currentUrls = photos.map((photo) => photo.publicUrl);
  const mediaChanged =
    !isRepost &&
    (currentUrls.length !== originalUrls.length ||
      currentUrls.some((url, index) => url !== originalUrls[index]));
  const resultValid = isRepost || trimmed.length > 0 || photos.length > 0;
  const canSave = resultValid && !update.isPending && !uploading;

  async function handleFileSelected(
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    const input = event.target;
    const file = input.files?.[0];
    input.value = "";
    if (!file || atCapacity || uploading) return;

    setUploadError(false);
    setUploading(true);
    try {
      const uploaded = await uploadPostPhoto(file);
      const photoId = `photo-${photoSeqRef.current++}`;
      setPhotos((previous) =>
        previous.length >= MAX_MEDIA_PER_POST
          ? previous
          : [
              ...previous,
              {
                id: photoId,
                publicUrl: uploaded.publicUrl,
                mediaType: "image",
              },
            ],
      );
    } catch {
      setUploadError(true);
    } finally {
      setUploading(false);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!canSave || update.isPending || uploading) return;

    const data: PostUpdate = {};
    if (contentChanged) data.content = trimmed;
    if (mediaChanged) data.media_urls = currentUrls;
    if (!contentChanged && !mediaChanged) {
      onClose();
      return;
    }
    update.mutate(
      { postId: post.post_id, data },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={update.isPending ? undefined : onClose}
      aria-labelledby="edit-post-title"
    >
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle id="edit-post-title" sx={{ pr: 7 }}>
          Edit post
        </DialogTitle>
        <IconButton
          type="button"
          aria-label="Close"
          title="Close"
          onClick={onClose}
          disabled={update.isPending}
          sx={{ position: "absolute", top: 10, right: 10 }}
        >
          <CloseRoundedIcon />
        </IconButton>
        <DialogContent dividers>
          <Stack spacing={2}>
            {update.isError || uploadError ? (
              <Alert severity="error" role="alert">
                {uploadError
                  ? "We couldn't upload that photo. Please try again."
                  : "We couldn't save your changes. Please try again."}
              </Alert>
            ) : null}
            <TextField
              autoFocus
              id="edit-post-content"
              label="Edit your post"
              value={content}
              onChange={(event) => setContent(event.target.value)}
              multiline
              minRows={5}
              fullWidth
            />

            {!isRepost ? (
              <Stack spacing={1.5}>
                {photos.some((item) => item.mediaType === "image") ? (
                  <ImageList cols={3} gap={8} sx={{ m: 0 }}>
                    {photos
                      .filter((item) => item.mediaType === "image")
                      .map((photo) => (
                        <ImageListItem key={photo.id}>
                          <Box
                            component="img"
                            src={photo.publicUrl}
                            alt="Post image"
                            sx={{
                              width: "100%",
                              aspectRatio: "1",
                              objectFit: "cover",
                              borderRadius: 1,
                            }}
                          />
                          <IconButton
                            type="button"
                            size="small"
                            aria-label="Remove image"
                            title="Remove image"
                            onClick={() =>
                              setPhotos((previous) =>
                                previous.filter((item) => item.id !== photo.id),
                              )
                            }
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              bgcolor: "background.paper",
                            }}
                          >
                            <CloseRoundedIcon fontSize="small" />
                          </IconButton>
                        </ImageListItem>
                      ))}
                  </ImageList>
                ) : null}
                {photos.some((item) => item.mediaType === "youtube") ? (
                  <Typography variant="body2" color="text.secondary">
                    Video attachments are kept when you edit this post.
                  </Typography>
                ) : null}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center" }}
                >
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    disabled={atCapacity || uploading}
                    startIcon={<AddPhotoAlternateOutlinedIcon />}
                  >
                    {uploading ? "Uploading…" : "Add photo"}
                    <Box
                      component="input"
                      id="edit-post-photo"
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelected}
                      disabled={atCapacity || uploading}
                      sx={{ display: "none" }}
                    />
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    {photos.length}/{MAX_MEDIA_PER_POST}
                  </Typography>
                </Stack>
              </Stack>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button type="submit" variant="contained" disabled={!canSave}>
            Save
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
